import { useState, useEffect, useContext } from 'react'
import { Grid, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography, IconButton, FormHelperText, Select, MenuItem } from '@mui/material'
import { LoadingButton } from '@mui/lab';
import UploadIcon from '@mui/icons-material/UploadFile'
import { handleFieldChange } from '../../form/Form.js';
import { checkFilenameConflict, conductFileResourceUpload, getSiteSettings, publishResourceLibraryDraft } from '../data/index.js';
import bus from '../../utils/EventBus.js'
import Event from '../../utils/EventNames.js'
import TagsFormControl from '../TagsFormControl.js';
import RoleContext from '../../auth/RoleContext.js';
import { ROLES } from '../../auth/role.js';
import CacheContext from '../../cache/CacheContext.js';
// import FeatureFlagsContext from '../../config/FeatureFlagsContext.js';

export default function SubmitResourceDialog(props={
  open: false, onClose: undefined, resourceCategories: [], 
  selectedCategory: undefined, 
  selectedFile: undefined
}) {
  const roleDetails = useContext(RoleContext)
  // const ff = useContext(FeatureFlagsContext)

  const {
    open, onClose, resourceCategories, 
    selectedCategory, 
    selectedFile
  } = props
  const [targetFile, setTargetFile] = useState()
  const [targetFileName, setTargetFileName] = useState('')
  const [targetFileLabel, setTargetFileLabel] = useState('')
  const [fileProperties, setFileProperties] = useState(null)
  const [resourceTags, setResourceTags] = useState(null)
  const [hasFilenameConflict, setHasFilenameConflict] = useState(false)

  const cacheContext = useContext(CacheContext)
  const selectFile = ((event) => {
    const _targetFile = event.target.files[0]
    if(_targetFile) {
      setTargetFile(_targetFile)
      setTargetFileName(_targetFile.name)
      setFileProperties({ ...fileProperties, fileName: _targetFile.name, fileSize: _targetFile.size})
      setTargetFileLabel(`${_targetFile.name} | ${_targetFile.size} bytes`)
      setHasFilenameConflict(false)
    }
  })

  const submitNewResource = async (withPublish = false) => {
    bus.emit(Event.SHOW_PROGRESS_MODAL)
    try {
      const conflictChecks = await Promise.all([checkFilenameConflict('Submitted',targetFile.name), checkFilenameConflict('Published',targetFile.name)])
      const hasConflict = conflictChecks.some((n) => n)
      if ( hasConflict ) {
        setHasFilenameConflict(true)
        throw new Error('File name conflict')
      }

      if (withPublish === true && roleDetails.hasRole([ROLES.ADMIN, ROLES.MFI_ADMIN, ROLES.QSE_SAFETY_MANAGER])) {
        const newResourceId = await conductFileResourceUpload(fileProperties, targetFile, Event.PROGRESS_MODAL_PROGRESS, false)
        await publishResourceLibraryDraft(newResourceId)
        dialogClose(false, true)
      } else {
        await conductFileResourceUpload(fileProperties, targetFile, Event.PROGRESS_MODAL_PROGRESS)
        dialogClose(true)
      }
    } catch (error) {

    } finally {
      bus.emit(Event.HIDE_PROGRESS_MODAL)
    }
  }

  const dialogClose = (navToMyResources, fileTouched) => {
    setTargetFile(null)
    setTargetFileName(null)
    setTargetFileLabel(null)
    setFileProperties(null)
    setHasFilenameConflict(false)
    onClose(navToMyResources, fileTouched)
  }

  useEffect(() => {
    let _fileProperties = {
      ...fileProperties
    }
    
    if (selectedFile) {
      setTargetFile(selectedFile)
      setTargetFileName(selectedFile.name)
      _fileProperties.fileName = selectedFile.name
      _fileProperties.fileSize = selectedFile.size
      setTargetFileLabel(`${selectedFile.name} | ${selectedFile.size} bytes`)
    }
    if (selectedCategory) {
      _fileProperties.category = selectedCategory
    }
    setFileProperties(_fileProperties)
  }, [selectedFile, selectedCategory])

  useEffect(() => {
    if (open) {
    (async () => {
      const settings = await getSiteSettings(cacheContext)
      const tags = settings.resources.tags
      setResourceTags(tags ?? null) 
    })()
    }
  }, [open])

  return (
    <Dialog open={open} fullWidth maxWidth='md'>
      <DialogTitle><Typography variant='body1' fontWeight={'bold'}>Submit a Resource to Share</Typography></DialogTitle>
      <DialogContent sx={{padding: 2}}>
        <Grid container rowGap={1.5} columnGap={1} alignItems={'center'}>
        <Grid item xs={4} sm={1.5} textAlign={'right'}>Category:</Grid>
        <Grid item xs={7} sm={3.5}>
          <Select multiple={false} 
            fullWidth
            value={fileProperties?.category || ''}
            required
            displayEmpty
            style={!fileProperties?.category ? { color: 'red'} : { color: 'inherit' }}
            onChange={(event) => handleFieldChange('category', event.target.value, fileProperties, setFileProperties)}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <em>Select a primary category</em>;
              }
  
              return selected
            }}
            >
            { Array.from(new Set([...resourceCategories, fileProperties?.category ?? null])).filter( c => c).map( (c, idx) => {
              return (
              <MenuItem key={idx} value={c}>{c}</MenuItem>)}
            ) }
          </Select>
          </Grid>
          <Grid item xs={4} sm={1} textAlign={'right'}>Tags:</Grid>
          <Grid item xs={7} sm={5}>
            { resourceTags &&
              <TagsFormControl tagChoices={[...resourceTags].sort((a,b) => a.localeCompare(b))} tags={fileProperties?.tags ?? []} setTags={(t) => handleFieldChange('tags', t, fileProperties, setFileProperties)} key={'tags'} />
            }
          </Grid>
          <Grid item xs={4} sm={1.5} textAlign={'right'}>Title:</Grid><Grid item xs={7} sm={9}>
            <Stack direction={'row'} alignItems={'end'} columnGap={1}>
              <TextField type='text' size='small' 
                style={{width: '300px'}}
                variant='outlined'
                value={fileProperties?.title}
                inputProps={{ maxLength: 45 }}
                onChange={(event) => handleFieldChange('title', event.target.value, fileProperties, setFileProperties)}
                />
              <FormHelperText>{(fileProperties?.title || '').length}/45</FormHelperText>
            </Stack>
          </Grid>
          <Grid item xs={4} sm={1.5} textAlign={'right'}>Description:</Grid><Grid item xs={7} sm={9}>
            <Stack direction={'row'} alignItems={'end'} columnGap={1}>
            <TextField type='text' multiline size='small' fullWidth 
              variant='outlined'
              value={fileProperties?.description}
              inputProps={{ maxLength: 200 }}
              onChange={(event) => handleFieldChange('description', event.target.value, fileProperties, setFileProperties)}
              />
              <FormHelperText>{(fileProperties?.description || '').length}/200</FormHelperText>
            </Stack>
          </Grid>
          { hasFilenameConflict && 
          <Grid item xs={12} padding={2} sx={{backgroundColor: 'darkorange'}}>
            <Typography variant='body1' color='black' fontWeight='bold' whiteSpace='pre-wrap'>
              A file with the same name already exists in the Resource Library ("{targetFileName}"). Please rename your file then attach again.
            </Typography> 
          </Grid>
          }
          <Grid item xs={4} sm={1.5} textAlign={'right'}>File:</Grid><Grid item xs={7} sm={9}>
            {/* <Stack direction='row' spacing={2}>         */}
                    <TextField      
                      type="text"
                      size='small'
                      placeholder="Select a File"
                      disabled
                      style={{ width: targetFileLabel ? `${Math.max(Math.ceil(targetFileLabel.length * 8), 300)}px` : '300px' }}
                      value={targetFileLabel}
                      InputProps={{
                        endAdornment: (
                          <IconButton component="label" size="small">
                            <UploadIcon />
                            <input 
                              styles={{display:"none"}} 
                              hidden 
                              type="file" 
                              onChange={selectFile}
                              size='small'
                              />                
                          </IconButton>
                        ),
                      }}
                    />
                  {/* </Stack> */}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: '1.25rem' }}>
        { roleDetails.hasRole([ROLES.QSE_SAFETY_MANAGER, ROLES.MFI_ADMIN, ROLES.ADMIN]) &&
          <LoadingButton disabled={ !(fileProperties?.category && targetFile) || hasFilenameConflict } onClick={() => { submitNewResource(true); }}>Submit and Approve</LoadingButton>
        }
        <LoadingButton disabled={ !(fileProperties?.category && targetFile) || hasFilenameConflict } onClick={() => { submitNewResource(); }}>Submit</LoadingButton>
        <Button onClick={() => { dialogClose(); }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};